import React, { useState } from 'react';
import Map, { ViewState, ViewStateChangeEvent } from 'react-map-gl';

import { MapViewState, PickingInfo, ViewStateChangeParameters } from '@deck.gl/core';
import { DeckGL } from '@deck.gl/react';
import { Box } from '@mui/joy';
import { observer } from 'mobx-react-lite';

import { config } from '../../config/runtimeConfig';
import { MissionPlanningService } from '../../services/MissionPlanningService';
import { missionPlannerStore, MissionTool } from '../../stores/MissionStore';
import { pilotTelemetryStore } from '../../stores/PilotTelemetryStore';
import { MissionActionType, GoToWaypointAction, TakeOffAction, Mission } from '../../types/Mission';
import { MapItem, MapItemType } from './MapItem';
import MissionItemParameters from './MissionItemParameters';
import MissionPlanningActionsToolbox from './MissionPlanningActionsToolbox';
import TopBar from './TopBar';
import WaypointsAndOrbitsLayer from './WaypointsAndOrbitsLayer';

const MissionManagerMap: React.FC = observer(() => {
  // Resolve Mapbox token from environment variables – supports Vite/CRA/Next public prefixes
  const MAPBOX_TOKEN = config.REACT_APP_MAPBOX_TOKEN;
  const MAP_STYLE = config.REACT_APP_MAP_STYLE;

  const [viewState, setViewState] = useState<MapViewState>({
    latitude: pilotTelemetryStore.telemetry.lat,
    longitude: pilotTelemetryStore.telemetry.lon,
    zoom: 16,
    bearing: 0,
    pitch: 0
  });

  const [selectedItem, setSelectedItem] = useState<MapItem | undefined>(undefined);
  const [hoveredItem, setHoveredItem] = useState<MapItem | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const [missionName, setMissionName] = useState('Untitled mission');

  const handleClick = ({ coordinate }: PickingInfo) => {
    const [lng, lat] = coordinate!;
    const tool = missionPlannerStore.activeTool;

    if (tool === MissionTool.TakeOff) {
      if (missionPlannerStore.missionActions[0]) {
        // The first action should be Take Off action, if exists
        missionPlannerStore.updateMissionAction(0, {
          location: [lng, lat],
          altitude: pilotTelemetryStore.telemetry.altitude,
          verticalSpeed: pilotTelemetryStore.telemetry.verticalSpeed
        } as TakeOffAction);
      } else {
        missionPlannerStore.addMissionAction(
          {
            type: 'TakeOff',
            location: [lng, lat],
            altitude: pilotTelemetryStore.telemetry.altitude,
            verticalSpeed: pilotTelemetryStore.telemetry.verticalSpeed
          } as TakeOffAction,
          0
        );
      }
    } else if (tool === MissionTool.AddWaypoint) {
      if (missionPlannerStore.missionActions[0]?.type !== MissionActionType.TakeOff)
        // The first action must be a Take Off action
        return;
      const lastWaypointOrOrbitAction = missionPlannerStore.missionActions.findLastIndex(
        (action) => action.type === MissionActionType.GoToWaypoint || MissionActionType.Orbit
      );
      missionPlannerStore.addMissionAction(
        {
          type: 'GoToWaypoint',
          location: [lng, lat],
          altitude: pilotTelemetryStore.telemetry.altitude,
          speed: pilotTelemetryStore.telemetry.horizontalSpeed
        } as GoToWaypointAction,
        lastWaypointOrOrbitAction + 1
      );
    } else if (tool === MissionTool.ReturnToBase) {
      // TODO
    } else if (tool === MissionTool.Land) {
      // TODO
    }
  };

  const takeOffAction =
    missionPlannerStore.missionActions[0]?.type === MissionActionType.TakeOff
      ? {
          latitude: (missionPlannerStore.missionActions[0] as TakeOffAction).location[1],
          longitude: (missionPlannerStore.missionActions[0] as TakeOffAction).location[0]
        }
      : undefined;

  const goToWaypointActions = missionPlannerStore.missionActions
    .filter((action) => action.type === MissionActionType.GoToWaypoint)
    .map((action) => ({
      latitude: (action as GoToWaypointAction).location[1],
      longitude: (action as GoToWaypointAction).location[0]
    }));

  const allItems: MapItem[] = [
    takeOffAction
      ? {
          type: MapItemType.Takeoff,
          index: 1,
          position: takeOffAction
        }
      : null,
    ...goToWaypointActions.map((waypoint, index) => ({
      type: MapItemType.Waypoint,
      index: index + 2,
      position: waypoint
    }))
  ].filter(Boolean) as MapItem[];

  const handleItemSelected = (item: MapItem) => {
    setSelectedItem(item);
  };

  const handleItemHover = (item: MapItem | undefined) => {
    setHoveredItem(item);
  };

  const isDeleteDisabled =
    selectedItem?.type === MapItemType.Takeoff &&
    missionPlannerStore.missionActions.some((action) => action.type !== MissionActionType.TakeOff);

  const hasTakeoff = !!takeOffAction;

  const handleItemDelete = (item: MapItem) => {
    if (item.type === MapItemType.Takeoff) {
      const hasWaypoints = missionPlannerStore.missionActions.some(
        (action) => action.type !== MissionActionType.TakeOff
      );

      if (hasWaypoints) {
        console.log('Cannot delete takeoff: there are other actions to be performed.');
        return;
      }

      missionPlannerStore.removeMissionItem(0);
    } else if (item.type === MapItemType.Waypoint && !!item.index) {
      const waypointActionIndex = missionPlannerStore.missionActions.findIndex(
        (action, index) => action.type === MissionActionType.GoToWaypoint && index === item.index! - 1
      );
      if (waypointActionIndex !== -1) {
        missionPlannerStore.removeMissionItem(waypointActionIndex);
      }
    }

    if (selectedItem === item) {
      setSelectedItem(undefined);
    }

    if (hoveredItem === item) {
      setHoveredItem(undefined);
    }
  };

  const handleItemDrag = (item: MapItem) => {
    if (item.type === MapItemType.Takeoff) {
      const takeoffIndex = 0;
      const updates: Partial<TakeOffAction> = {
        location: [item.position.longitude, item.position.latitude]
      };
      missionPlannerStore.updateMissionAction(takeoffIndex, updates);
    } else if (item.type === MapItemType.Waypoint) {
      const waypointActionIndex = missionPlannerStore.missionActions.findIndex(
        (action, index) => action.type === MissionActionType.GoToWaypoint && index === item.index! - 1
      );

      if (waypointActionIndex !== -1) {
        const updates: Partial<GoToWaypointAction> = {
          location: [item.position.longitude, item.position.latitude]
        };
        missionPlannerStore.updateMissionAction(waypointActionIndex, updates);
      }
    }
  };

  const handleDragStateChange = (dragging: boolean) => {
    setIsDragging(dragging);
  };

  const getMission = (): Mission => {
    return {
      name: missionName,
      actions: missionPlannerStore.missionActions
    };
  };

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <DeckGL
        controller={!isDragging}
        style={{ position: 'absolute', width: '100%', height: '100%', inset: '0', zIndex: '2' }}
        initialViewState={viewState}
        viewState={viewState}
        onViewStateChange={(e: ViewStateChangeParameters) => setViewState(e.viewState)}
        layers={[
          new WaypointsAndOrbitsLayer({
            takeOffPoint: takeOffAction,
            waypoints: goToWaypointActions,
            selectedItemIndex: selectedItem?.index,
            hoveredItem,
            onItemSelected: handleItemSelected,
            onItemDrag: handleItemDrag,
            onDragStateChange: handleDragStateChange,
            onItemHover: handleItemHover
          })
        ]}
        onClick={handleClick}
      >
        <Map
          mapboxAccessToken={MAPBOX_TOKEN}
          mapStyle={MAP_STYLE}
          {...viewState}
          onMove={(e: ViewStateChangeEvent) => setViewState(e.viewState as ViewState)}
          style={{ position: 'absolute', width: '100%', height: '100%', inset: '0', zIndex: '1' }}
          cursor='grab'
        />
      </DeckGL>

      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          top: 0,
          left: 0,
          zIndex: 3
        }}
      >
        <TopBar
          onMenuClick={() => console.log('Menu clicked')}
          onNewMissionClick={() => console.log('New Mission clicked')}
          onOpenMissionClick={() => console.log('Open Mission clicked')}
          onSaveMissionClick={() => MissionPlanningService.saveMission(getMission())}
          missionName={missionName}
          onMissionNameChange={setMissionName}
          saveEnabled={missionPlannerStore.missionActions.length > 0}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          zIndex: 3,
          display: 'flex',
          gap: 1
        }}
      >
        <MissionPlanningActionsToolbox hasTakeoff={hasTakeoff} />
        {selectedItem && (
          <MissionItemParameters
            items={allItems}
            selectedItem={selectedItem}
            hoveredItem={hoveredItem}
            onItemSelect={handleItemSelected}
            onItemHover={handleItemHover}
            onItemDelete={handleItemDelete}
            onClose={() => setSelectedItem(undefined)}
            isDeleteDisabled={isDeleteDisabled}
          />
        )}
      </Box>
    </Box>
  );
});

export default MissionManagerMap;
